import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson } from '../lib/api';

// --- Types ---

export type QueueItemType = 'task' | 'follow_up' | 'renewal' | 'case' | 'deal' | 'approval';

export type QueueItemPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface QueueItem {
  id: string;
  type: QueueItemType;
  title: string;
  description?: string;
  entityId?: string;
  entityType?: string;
  entityName?: string;
  priority: QueueItemPriority;
  dueDate?: string;
  isOverdue: boolean;
  isDueToday: boolean;
  snoozedUntil?: string;
  dismissedAt?: string;
  createdAt: string;
}

export interface ActivityQueueResponse {
  items: QueueItem[];
  total: number;
  overdueCount: number;
  dueTodayCount: number;
}

// --- Hooks ---

export function useActivityQueue() {
  return useQuery({
    queryKey: ['activity-queue'],
    queryFn: () => fetchJson<ActivityQueueResponse>('/api/activity-queue'),
    refetchInterval: 60_000,
  });
}

export function useDismissQueueItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, snoozeMinutes }: { id: string; snoozeMinutes?: number }) =>
      postJson<void>(`/api/activity-queue/${id}/dismiss`, { snoozeMinutes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-queue'] });
    },
  });
}
